// AIDEV-NOTE: Stale-PWA recovery primitives. On every deploy Vite rotates the
// content-hash on code-split chunks and Vercel purges the old ones, so a client
// still running the previous build 404s when it lazy-imports a now-missing chunk
// ("Failed to fetch dynamically imported module"). These helpers let us reload
// onto the fresh build — but NEVER while a recording/upload is in flight, which a
// reload would destroy. Used by entry.client.tsx (reactive vite:preloadError) and
// app-update-prompt.tsx (proactive version poll).

// Window event the reactive handler dispatches when it wants to reload but is
// blocked by an in-flight operation; AppUpdatePrompt surfaces the prompt instead.
export const UPDATE_AVAILABLE_EVENT = "pc:update-available";

// Keyed blockers so independent in-flight operations (recording, upload) can each
// veto an auto-reload without clobbering one another.
const reloadBlockers = new Set<string>();

export function setReloadBlocked(key: string, blocked: boolean): void {
    if (blocked) {
        reloadBlockers.add(key);
    } else {
        reloadBlockers.delete(key);
    }
}

export function isReloadBlocked(): boolean {
    return reloadBlockers.size > 0;
}

const LAST_RELOAD_KEY = "pc:last-auto-reload";
const RELOAD_DEBOUNCE_MS = 10_000;

// Reloads onto the latest build if it is safe to do so. Returns false (without
// reloading) when an operation is in flight or when we reloaded very recently
// (loop guard against a chunk that is genuinely, permanently gone).
export function safeReload(): boolean {
    if (typeof window === "undefined") return false;
    if (isReloadBlocked()) return false;

    try {
        const last = Number(
            window.sessionStorage.getItem(LAST_RELOAD_KEY) ?? 0,
        );
        if (Date.now() - last < RELOAD_DEBOUNCE_MS) return false;
        window.sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));
    } catch {
        // sessionStorage can throw in privacy modes; proceed without the guard.
    }

    window.location.reload();
    return true;
}
